import { type CSSProperties } from 'react';
import { type AvatarData } from '@/api';
import { assetsUrl } from '../../../consts/env';
import { normalizeAvatarPart } from './constants/avatar.constants';

/**
 * 아바타 파츠 레이어 조합 규칙 (SpriteAvatar / FullBodyAvatar / AnimatedAvatar 공통).
 *
 * - skin → clothes → face → hair → mask → glasses 순서로 아래에서 위로 쌓는다.
 * - accessory(mask/glasses)는 0/null/undefined 면 제외한다.
 * - 각 파츠 sprite URL: {assetsUrl}/static/assets/sprites/{part}/-{id}.png
 *
 * "상반신으로 crop / 전신 프레임 / 애니메이션" 같은 렌더 방식은 각 컴포넌트가 담당하고,
 * 여기서는 어떤 레이어를 어떤 순서/URL 로 쌓을지(코어 조합 로직)만 책임진다. 파츠가
 * 추가되거나 레이어 순서가 바뀔 때 이 파일 한 곳만 고치면 된다.
 */
export const AVATAR_BASE_PARTS = ['skin', 'clothes', 'face', 'hair'] as const;
export const AVATAR_ACCESSORY_PARTS = ['mask', 'glasses'] as const;
export function getAvatarSpriteUrl(part: string, id: number) {
  return `${assetsUrl}/static/assets/sprites/${part}/-${id}.png`;
}
function normalizeAvatarAccessoryId(value: number | null | undefined) {
  if (typeof value !== 'number' || !Number.isFinite(value) || value === 0) {
    return 0;
  }
  return Math.floor(Math.abs(value));
}
export type AvatarSpriteLayer = {
  /** 슬롯 이름(skin/clothes/.../glasses). 유일하므로 React key 로도 쓴다. */
  part: string;
  id: number;
  url: string;
};

/** 합성 순서대로 정렬된 레이어 목록. 미착용 accessory 슬롯은 제외된다. */
export function buildAvatarSpriteLayers(avatarData: AvatarData): AvatarSpriteLayer[] {
  const baseIds: Record<(typeof AVATAR_BASE_PARTS)[number], number> = {
    skin: normalizeAvatarPart(avatarData.skinId),
    clothes: normalizeAvatarPart(avatarData.clothesId),
    face: normalizeAvatarPart(avatarData.faceId),
    hair: normalizeAvatarPart(avatarData.hairId)
  };
  const base: AvatarSpriteLayer[] = AVATAR_BASE_PARTS.map(part => ({
    part,
    id: baseIds[part],
    url: getAvatarSpriteUrl(part, baseIds[part])
  }));
  const accessories: AvatarSpriteLayer[] = AVATAR_ACCESSORY_PARTS.flatMap(slot => {
    const id = normalizeAvatarAccessoryId(avatarData.accessoryIds?.[slot]);
    return id > 0 ? [{
      part: slot,
      id,
      url: getAvatarSpriteUrl(slot, id)
    }] : [];
  });
  return [...base, ...accessories];
}

/**
 * background-image 방식으로 파츠 레이어를 겹쳐 그리는 공통 렌더러
 * (SpriteAvatar / FullBodyAvatar 용). 위치/스케일별 차이는 `layerStyle` 로 주입한다.
 * (AnimatedAvatar 는 scss + `--image-url` 애니메이션 방식이라 buildAvatarSpriteLayers 만 사용)
 */
export function AvatarSpriteLayers({
  avatarData,
  layerStyle
}: {
  avatarData: AvatarData;
  layerStyle: CSSProperties;
}) {
  return <>
      {buildAvatarSpriteLayers(avatarData).map(({
      part,
      url
    }) => <span key={part} className="absolute inset-0" style={{
      backgroundImage: `url(${url})`,
      ...layerStyle
    }} aria-hidden="true" />)}
    </>;
}